import { Alert, Anchor, Badge, List, Stack, Switch, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { denar, stevilo, tocke } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { seja } from '../seja/seja';

/**
 * Nastavitve → Umetna inteligenca (novo v spletu): vklop za podjetje (privzeto vklopljeno), kaj počne, kdo obdela
 * podatke, cena (odšteje se od točk ob vsaki uporabi, brez podrobnosti cenika), stanje točk in poraba v tem
 * mesecu. Izklop skrije funkcije z umetno inteligenco za vse uporabnike podjetja.
 */
export function UmetnaInteligenca() {
  const odjemalec = useQueryClient();
  const stanje = useQuery({
    queryKey: ['ai-stanje'],
    queryFn: ({ signal }) => podatki(api.GET('/api/ai/stanje', { signal })),
  });
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  if (!stanje.data) {
    return (
      <StanjeNalaganja
        napaka={stanje.error}
        ponovi={() => void stanje.refetch()}
        naslov="Nastavitve umetne inteligence ni bilo mogoče naložiti"
      />
    );
  }
  const s = stanje.data;

  const vklop = async (vklopljeno: boolean) => {
    setNapaka(null);
    setDela(true);
    try {
      odjemalec.setQueryData(['ai-stanje'], await podatki(api.PUT('/api/ai/vklop', { body: { vklopljeno } })));
      // seja pove, ali naj zasloni prikažejo funkcije z umetno inteligenco
      await seja.osvezi();
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="md">
      <Kartica
        naslov="Umetna inteligenca"
        dejanja={
          s.prepovedano ? (
            <Badge color="gray" variant="light">
              ni na voljo
            </Badge>
          ) : s.naVoljo ? (
            <Badge color="green" variant="light">
              deluje
            </Badge>
          ) : (
            <Badge color="yellow" variant="light">
              {s.vklopljeno ? 'ne deluje' : 'izklopljena'}
            </Badge>
          )
        }
      >
        <Stack gap="sm">
          {s.prepovedano ? (
            <Alert color="gray" variant="light">
              Umetna inteligenca za vaše podjetje ni na voljo. Za več informacij nam pišite prek povratnih informacij.
            </Alert>
          ) : (
            <Switch
              label="Uporabljaj umetno inteligenco"
              description="Izklop skrije funkcije z umetno inteligenco za vse uporabnike podjetja."
              checked={s.vklopljeno}
              disabled={dela}
              onChange={(e) => void vklop(e.currentTarget.checked)}
            />
          )}
          {!s.prepovedano && s.vklopljeno && !s.naVoljo && s.razlog && (
            <Alert color="yellow" variant="light" role="alert">
              {s.razlog}
            </Alert>
          )}
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Text size="sm" fw={600}>
            Kaj zna
          </Text>
          <List size="sm" spacing={4}>
            <List.Item>
              <b>Branje dokumentov</b> pri dodajanju vozila: iz fotografije prometnega dovoljenja, COC, pogodbe ali
              računa izpolni podatke vozila, nakupa in prodajalca. Dokumenti se shranijo k vozilu.
            </List.Item>
            <List.Item>
              <b>Opis vozila</b> pri urejanju vozila: iz podatkov vozila, vaših zapiskov in po želji fotografij napiše
              prodajni opis po navodilih iz{' '}
              <Anchor component={Link} to="/nastavitve/opis-vozil" size="sm">
                Nastavitev → Opis vozil
              </Anchor>
              .
            </List.Item>
            <List.Item>
              <b>Urejanje slik</b> pri sliki vozila: registrsko tablico zamenja z vašim logotipom, odstrani moteče
              predmete, izboljša svetlobo ali vozilo postavi v studio ali vaš salon – po izbirah iz{' '}
              <Anchor component={Link} to="/nastavitve/urejanje-slik" size="sm">
                Nastavitev → Urejanje slik
              </Anchor>
              . Vozilo ostane nespremenjeno; urejeno sliko pred zamenjavo primerjate z izvirnikom.
            </List.Item>
          </List>
          <Text size="sm" fw={600}>
            Cena in podatki
          </Text>
          <Text size="sm">
            Vsaka uporaba se sproti odšteje od točk glede na dejansko porabo – branje enega dokumenta običajno stane
            delček točke, približna cena urejanja slike je napisana ob gumbu; znesek posamezne uporabe vidite v
            zgodovini točk. Uporaba je mogoča, dokler imate kaj točk (stanje ne gre v minus); točke naložite na zaslonu{' '}
            <Anchor component={Link} to="/narocnina" size="sm">
              Naročnina
            </Anchor>
            .
          </Text>
          <Text size="sm" c="dimmed">
            Dokumente in slike obdela Google (Gemini) kot pogodbeni obdelovalec; Google jih ne uporablja za učenje
            svojih modelov.
          </Text>
        </Stack>
      </Kartica>
      <Kartica naslov="Poraba v tem mesecu">
        <Podatki
          vrstice={[
            ['Stanje točk', tocke(s.tocke)],
            ['Uporab', stevilo(s.klicevMesec ?? 0)],
            [
              'Porabljeno',
              `${tocke(s.zaracunanoMesec ?? 0)} točk${s.omejitevMesec ? ` od največ ${denar(s.omejitevMesec)} na mesec` : ''}`,
            ],
          ]}
        />
      </Kartica>
    </Stack>
  );
}
